/* eslint-disable @typescript-eslint/no-unsafe-assignment -- react-select types are bs */

import { forwardRef, useEffect, useMemo, useRef } from "react";
import type { FC, ChangeEvent, ReactNode } from "react";
import type {
  ActionMeta,
  CSSObjectWithLabel,
  FormatOptionLabelMeta,
  GroupBase,
  MenuPlacement,
  OptionProps,
  Options,
  SelectComponentsConfig,
  StylesConfig,
  ThemeConfig
} from "react-select";
import ReactSelect, { components as reactSelectComponents } from "react-select";
import { cx } from "../../../utils/classnames";
import { InputSize } from "../Input";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../Tooltip";

export const inputSize = {
  [InputSize.BASE]: {
    height: 48
  },
  [InputSize.SM]: {
    height: 40
  }
};

export interface BaseOption {
  id?: string | number; // Add this line
  label: string | JSX.Element;
  value: string | GroupBase<string | number> | number | boolean | undefined;
  isDisabled?: boolean;
  disabledMessage?: string;
}

interface GroupOption {
  label: string;
  options: BaseOption[];
}

export type Option = BaseOption | GroupOption;

interface ExtendedChangeEvent extends ChangeEvent<HTMLSelectElement> {
  id?: string | number;
}

export interface SelectProps {
  label?: string | JSX.Element;
  id: string;
  placeholder?: string;
  size?: InputSize;
  value?: string | number | boolean | (string | number)[];
  onChange?: (
    e: ExtendedChangeEvent,
    action?: ActionMeta<string | number | Option>
  ) => void;
  options: Option[];
  className?: string;
  labelClassName?: string;
  defaultValue?: string | number | (string | number)[] | Option[];
  loading?: boolean;
  searchable?: boolean;
  required?: boolean;
  hideOptional?: boolean;
  error?: string | boolean;
  clearable?: boolean;
  isDisabled?: boolean;
  focusOnError?: boolean;
  creatable?: boolean;
  onInputChange?: (inputValue: string, actionMeta: unknown) => void;
  handleCreate?: (inputValue: string) => void;
  formatCreateLabel?: (inputValue: string) => ReactNode;
  createOptionPosition?: "first" | "last";
  menuPosition?: "fixed" | "absolute";
  menuPlacement?: MenuPlacement;
  menuShouldScrollIntoView?: boolean;
  menuPortalTarget?: HTMLElement;
  disableFuzzyFiltering?: boolean;
  defaultInputValue?: string;
  styles?: StylesConfig;
  formatOptionLabel?: (
    data: Option,
    context: FormatOptionLabelMeta<Option>
  ) => ReactNode;
  // For fixed width of menu
  menuWidth?: string;
  showErrorMessage?: boolean;
  isMulti?: boolean;
  components?: SelectComponentsConfig<unknown, boolean, GroupBase<unknown>>;

  nativeMobileSelect?: boolean;

  /**
   * Legacy prop for margin bottom.
   * Why?
   * When developing this input I accidentally added margin bottom input instead of mt to error message.
   * This created wrong visual sizing of the input.
   */
  legacyErrorMargins?: boolean;
}

// Please, use sizedSelectStyles() instead of defaultSelectStyles
export const defaultSelectStyles: StylesConfig<
  string | number | Option,
  boolean,
  GroupBase<string | number | Option>
> = {
  indicatorSeparator: () => ({
    display: "none"
  }),
  option: (styles: CSSObjectWithLabel, state) => {
    let cursor = "pointer";
    let color = "#1C1D22";
    let backgroundColor = "#ffffff";

    if (state.isDisabled) {
      cursor = "not-allowed";
      color = "#BEC0CA";
      backgroundColor = "#1C1D220A";
    } else if (state.isSelected) {
      color = "#ffffff";
      backgroundColor = "#E6007E";
    } else if (state.isFocused) {
      backgroundColor = "#1C1D221A";
    }

    return {
      ...styles,
      cursor,
      zIndex: 52,
      color,
      backgroundColor
    };
  },
  multiValue: (styles: CSSObjectWithLabel) => ({
    ...styles,
    backgroundColor: "#1C1D220A", // alpha-40
    borderRadius: "9999px",
    padding: "0px 8px"
  }),
  multiValueLabel: (styles: CSSObjectWithLabel) => ({
    ...styles,
    color: "#37393F", // content-700
    fontWeight: 500,
    fontSize: 13,
    padding: "0px"
  }),
  multiValueRemove: (styles: CSSObjectWithLabel) => ({
    ...styles,
    color: "#37393F", // content-700
    padding: "0px 4px"
  }),
  control: (styles: CSSObjectWithLabel, state) => ({
    ...styles,
    cursor: "pointer",
    boxShadow: state.isFocused
      ? "0px 0px 0px 2px #FFF4FA"
      : "0px 1px 2px 0px rgba(22,106,135,0.05)",
    minHeight: 48,
    boxSizing: "border-box",
    height: 48,
    borderColor: "transparent"
  }),
  dropdownIndicator: (defaultStyles: CSSObjectWithLabel) => {
    return {
      ...defaultStyles,
      paddingLeft: 2
    };
  },
  valueContainer: (defaultStyles: CSSObjectWithLabel) => {
    return {
      ...defaultStyles,
      color: "#1C1D22",
      fontWeight: 500,
      paddingLeft: 12,
      fontSize: 16,
      paddingRight: 0
    };
  },
  input: (defaultStyles: CSSObjectWithLabel) => {
    return {
      ...defaultStyles,
      "input:focus": {
        boxShadow: "none"
      }
    };
  },
  placeholder: (defaultStyles: CSSObjectWithLabel) => {
    return {
      ...defaultStyles,
      color: "#BEC0CA",
      fontSize: 16
    };
  },
  // Actually changes color of selected item in input if no input is present
  singleValue: (defaultStyles, state) => ({
    ...defaultStyles,
    color: state.isDisabled ? "#BEC0CA" : "#1C1D22"
  }),

  menu: (defaultStyles) => ({
    ...defaultStyles,
    zIndex: 11 // style the menu when it's portalled into the DOM node given to `menuPortalTarget`
  }),
  menuPortal: (defaultStyles) => ({
    ...defaultStyles,
    // Portal are mostly used for modals, so we need to set a higher z-index
    zIndex: 50 // style the menu when it's portalled into the DOM node given to `menuPortalTarget`
  }),
  groupHeading: (defaultStyles) => ({
    ...defaultStyles,
    paddingTop: 16,
    fontSize: 13,
    fontWeight: 500,
    color: "#A6A8B1",
    paddingBottom: 8,
    textTransform: "none",
    borderBottom: "1px solid #1C1D220A"
  })
};

export const sizedSelectStyles = (
  size?: InputSize,
  menuWidth?: string
): StylesConfig<unknown, boolean, GroupBase<unknown>> => {
  const ret: StylesConfig<unknown, boolean, GroupBase<unknown>> = {
    ...defaultSelectStyles,
    control: (
      styles: CSSObjectWithLabel,
      state: {
        isDisabled: boolean;
        selectProps: { error?: boolean; className?: string };
        isMulti: boolean;
      }
    ) => {
      return {
        ...styles,
        cursor: "pointer",
        minHeight: inputSize[size || InputSize.BASE].height,
        height: state.isMulti
          ? "auto"
          : inputSize[size || InputSize.BASE].height,
        maxHeight: state.isMulti
          ? "auto"
          : inputSize[size || InputSize.BASE].height,
        borderColor:
          state.selectProps.error ||
          (state.selectProps.className || "").includes("border-danger-500")
            ? "#F4423F"
            : "#1C1D221A",
        borderRadius: 6,
        backgroundColor: state.isDisabled ? "rgba(28,29,34,0.040)" : "#fff",
        boxSizing: "border-box"
      };
    }
  } as StylesConfig<unknown, boolean, GroupBase<unknown>>;

  if (menuWidth) {
    ret.menu = (styles: CSSObjectWithLabel) => ({
      ...styles,
      width: menuWidth
    });
  }

  return ret;
};

export const defaultSelectTheme: ThemeConfig = (theme) => ({
  ...theme,
  borderRadius: 6,
  colors: {
    ...theme.colors,
    primary25: "var(--primary-20)",
    primary50: "var(--primary-200",
    primary75: "var(--primary-400)",
    primary: "var(--primary-100)",
    dangerLight: "#fdc9c9",
    danger: "#f85252",
    neutral0: "#fff",
    neutral5: "#fdfdfd",
    neutral10: "transparent",
    neutral20: "rgba(28,29,34,0.100)",
    neutral30: "rgba(28,29,34,0.200)",
    neutral40: "#53545c",
    neutral50: "#45464e",
    neutral60: "#37393f",
    neutral70: "#33343a",
    neutral80: "#2c2d33",
    neutral90: "#1c1d22"
  }
});

const CustomOption: FC<OptionProps> = (props) => {
  const { data } = props as {
    data: BaseOption;
  };

  return props.isDisabled && data.disabledMessage ? (
    <Tooltip placement="bottom-start">
      <TooltipTrigger className="text-left w-full">
        <reactSelectComponents.Option {...props}>
          {props.children}
        </reactSelectComponents.Option>
      </TooltipTrigger>
      <TooltipContent className="z-50 max-w-[370px] rounded-lg border bg-alpha-800 p-4 text-sm text-white">
        {data.disabledMessage}
      </TooltipContent>
    </Tooltip>
  ) : (
    <reactSelectComponents.Option {...props}>
      {props.children}
    </reactSelectComponents.Option>
  );
};

const SelectWithRef: FC<SelectProps> = (
  {
    label,
    id,
    placeholder,
    onChange,
    value,
    options,
    className,
    defaultValue,
    labelClassName,
    loading,
    size = InputSize.BASE,
    searchable,
    error,
    clearable,
    focusOnError,
    creatable,
    required,
    hideOptional,
    disableFuzzyFiltering,
    defaultInputValue,
    styles,
    formatOptionLabel,
    menuWidth,
    showErrorMessage,
    isMulti = false,
    components,
    legacyErrorMargins = true,
    nativeMobileSelect = false,
    ...rest
  },
  _ref
) => {
  if (creatable && process.env.NODE_ENV !== "production") {
    throw new Error(
      "Cannot make Select creatable. Please use CreatableSelect component instead. \n \nValid import directories: \n - import { CreatableSelect } from 'shared/components/forms/Select/creatable';"
    );
  }

  const fallbackRef = useRef(null);
  const ref = _ref || fallbackRef;

  useEffect(() => {
    if (error && focusOnError) {
      (
        ref as unknown as {
          current?: {
            focus?: () => void;
          };
        }
      ).current?.focus?.();
    }
  }, [error, focusOnError]);

  const mappedOptions = useMemo(() => {
    return options.reduce((opts: BaseOption[], option: Option) => {
      if ("options" in option) return [...opts, ...option.options];
      return [...opts, option];
    }, []);
  }, []);

  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    ) && nativeMobileSelect;

  const inputClassName = useMemo(() => {
    return cx(
      "placeholder:text-content-100 block cursor-pointer rounded-md text-base placeholder:text-base placeholder:font-normal",
      error ? "border-danger-500" : "border-alpha-100",
      showErrorMessage || !legacyErrorMargins ? "" : "mb-2",
      isMobile
        ? "w-full focus:border-primary-100 focus:shadow-focus-ring focus:outline-0 focus:ring-0 font-medium p-2 px-3 has-[option[value='']:checked]:text-content-100"
        : "",
      isMobile && size === InputSize.BASE ? "h-12" : "",
      isMobile && size === InputSize.SM ? "h-10" : "",
      className
    );
  }, [showErrorMessage, legacyErrorMargins, error, className, isMobile, size]);

  return (
    <>
      {label ? (
        <label
          className={cx(
            "text-content-700 mb-1 mr-auto block text-sm font-medium",
            labelClassName
          )}
          htmlFor={id}
        >
          {label}
          {!required && !hideOptional ? (
            <span className="text-content-100"> (optional)</span>
          ) : null}
        </label>
      ) : null}
      {isMobile ? (
        <select
          className={inputClassName}
          id={id}
          name={id}
          value={(value ?? "") as string}
          onChange={(e) => {
            if (onChange) {
              onChange(e);
            }
          }}
          disabled={loading}
        >
          <option value="" hidden>
            {placeholder}
          </option>
          {options.map((option) => {
            if ("options" in option) {
              return (
                <optgroup key={option.label} label={option.label}>
                  {option.options.map((opt) => (
                    <option
                      key={opt.value as string}
                      value={opt.value as string}
                      disabled={opt.isDisabled}
                    >
                      {opt.label}
                    </option>
                  ))}
                </optgroup>
              );
            }
            return (
              <option
                key={option.value as string}
                value={option.value as string}
                disabled={option.isDisabled}
              >
                {option.label}
              </option>
            );
          })}
        </select>
      ) : (
        <ReactSelect
          className={inputClassName}
          defaultInputValue={defaultInputValue}
          defaultValue={
            mappedOptions.find((d) => d.value === defaultValue) || defaultValue
          }
          filterOption={disableFuzzyFiltering ? (): true => true : undefined}
          id={id}
          instanceId={id}
          isClearable={clearable}
          isLoading={loading}
          isMulti={isMulti}
          isSearchable={searchable}
          name={id}
          onChange={(v, action) => {
            if (v && onChange) {
              const currentId = id ? id : (v as BaseOption).id || undefined;
              onChange(
                {
                  // @ts-expect-error -- react-select types are bs
                  target: { value: v.value },
                  id: currentId,
                  name: id
                },
                action
              );
            }
          }}
          components={{ Option: CustomOption, ...components }}
          options={options as unknown[] as Options<string | number>}
          placeholder={placeholder}
          theme={defaultSelectTheme}
          value={
            Array.isArray(value)
              ? mappedOptions.filter((d) =>
                  value.includes(d.value as string | number)
                )
              : mappedOptions.find((d) => d.value === value)
          }
          ref={ref}
          styles={{ ...sizedSelectStyles(size, menuWidth), ...styles }}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- need it, because Option types are incompatible
          formatOptionLabel={formatOptionLabel as any}
          aria-invalid={Boolean(error)}
          {...rest}
        />
      )}

      {showErrorMessage && error ? (
        <p
          className={cx(
            legacyErrorMargins ? "" : "mt-2",
            "text-danger-500 text-xs"
          )}
        >
          {error}
        </p>
      ) : null}
    </>
  );
};

// @ts-expect-error -- react-select types are bs
export const Select = forwardRef(SelectWithRef);
